/**
 * The Phonak One-Time Courtesy Replacement Request Form, docked on a replacement request (2026-10-29).
 *
 * Three steps, in order: prepare the form (eMerge fills Phonak's PDF), upload the signed scan, send it.
 * The server decides every refusal; this panel shows its words. Sending has three server modes
 * (PHONAK_REPLACEMENT_SEND): off (nothing is sent), review (the email goes ONLY to the review address) and
 * live (to the Phonak contact, copy to the provider and the sender). The panel says which mode it is in
 * and never words a review send as if it reached Phonak.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  phonakFormApi,
  type PhonakAccount, type PhonakDoc, type PhonakEar, type PhonakFacts, type PhonakStatus,
} from './phonakFormApi';

const REVIEW_ADDRESS = 'icotto@emergesc.com';
const SIGNED_MAX_BYTES = 2.5 * 1024 * 1024;
const CIRC_MAX = 2000;

const day = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
};

/** One key per click attempt, so a double click makes one form / one upload / one email. */
const newKey = () => {
  const c: any = typeof globalThis !== 'undefined' ? (globalThis as any).crypto : null;
  return c && typeof c.randomUUID === 'function' ? c.randomUUID() : `pf-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * What the desk's Phonak column and the sent line say about a send. Live wins over review. A send that did
 * not go (FAILED) is not a fact; one eMerge could not confirm (UNKNOWN) reads "may have been sent".
 */
export function phonakFactWords(p: Pick<PhonakFacts, 'last_review' | 'last_live'> | null | undefined): string | null {
  if (!p) return null;
  const live = p.last_live && p.last_live.outcome !== 'FAILED' ? p.last_live : null;
  if (live) return live.outcome === 'UNKNOWN' ? `May have been sent to Phonak ${day(live.at)} (live)` : `Sent to Phonak ${day(live.at)} (live)`;
  const rev = p.last_review && p.last_review.outcome !== 'FAILED' ? p.last_review : null;
  if (rev) {
    const where = `(review: went only to ${REVIEW_ADDRESS})`;
    return rev.outcome === 'UNKNOWN' ? `Review copy may have been sent ${day(rev.at)} ${where}` : `Review copy sent ${day(rev.at)} ${where}`;
  }
  return null;
}

/** Where a request stands for the column: nothing, prepared, signed, or sent (live or review). */
export function phonakStage(p: PhonakFacts | null | undefined): 'none' | 'prepared' | 'signed' | 'sent' {
  if (!p) return 'none';
  if (phonakFactWords(p)) return 'sent';
  if (p.signed_at) return 'signed';
  if (p.prepared_at) return 'prepared';
  return 'none';
}

/** The server's refusals in the words a person acts on; the server's own sentence wins when it sent one. */
const REFUSAL_FALLBACK: Record<string, string> = {
  PHONAK_SETTINGS_MISSING: 'The Phonak account details are not configured yet, so the form cannot be filled. Isaiah supplies them.',
  PHONAK_FORM_INCOMPLETE: 'The Phonak form is not ready. Check the required fields.',
  SENSITIVE_NUMBER_REFUSED: 'One field looks like it holds a Social Security, Medicaid, card or bank account number. Those never go on this form or into eMerge. Take it out and try again.',
  UNSUPPORTED_CHARACTERS: 'The form cannot print some of those characters. Correct the spelling and try again.',
  PHONAK_FIELD_TOO_LONG: 'One field is too long for its line on the form. Shorten it and try again.',
  REQUEST_CANCELLED: 'This replacement request was withdrawn, so the form cannot be prepared.',
  PATIENT_NAME_MISSING: 'eMerge holds no name for this resident yet, so the form cannot be filled.',
  CONFIRM_SIGNED_REQUIRED: 'Confirm that this is the prepared form, signed by the patient or responsible party and by the hearing care professional.',
  FILE_TOO_LARGE: 'The signed scan is larger than 2.5 MB, which is too large to email. Scan it again in black and white or at a lower resolution, as a PDF.',
  SIGNED_NOT_A_PDF: 'The signed form must be a PDF. Scan it to PDF and upload that.',
  PREPARED_NOT_FOUND: 'That prepared form is not on this request. Reload and try again.',
  PHONAK_SEND_OFF: 'Sending the Phonak form from eMerge is not switched on yet. Nothing was sent.',
  SEND_IN_FLIGHT: 'This form is being sent right now. Reload in a moment.',
  STALE_DOCUMENT: 'A newer signed form exists. The page was reloaded: check it and send again.',
  NO_PROVIDER: 'No hearing care professional is on file for this request, so eMerge cannot send it.',
  NO_WORK_EMAIL: 'The hearing care professional has no work email on file, so eMerge cannot send it.',
  RECIPIENT_SUPPRESSED: 'An address on this email is on the suppression list, so nothing was sent.',
  PHONAK_RECIPIENT_INVALID: 'The Phonak contact address on this server is not valid, so nothing was sent. Tell Isaiah.',
  MAIL_NOT_AVAILABLE: 'This server cannot send email right now. Nothing was sent.',
  NOT_FOUND: 'eMerge no longer finds this request. Reload the list.',
};
type Failed = { code?: string; message?: string; error?: string; status?: number };
const refusalWords = (r: Failed, fallback: string) =>
  (r.message && r.message !== 'Request failed' ? r.message : null) || (r.code && REFUSAL_FALLBACK[r.code]) || r.error || fallback;

/** Why this provider is the one copied. The server picks; nobody chooses here. */
const PROVIDER_SOURCE: Record<string, string> = {
  FILED_THE_REQUEST: 'who filed this request from the building',
  MARKED_ON_MANIFEST: 'the provider of the manifest that marked it REPLACEMENT',
  COVERS_THE_BUILDING: "the provider who covers the resident's building",
};

const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--muted)', minWidth: 0 };
const gridStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 };
const stepStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 10, padding: '10px 0', borderTop: '1px solid var(--border, #d9d9d9)' };

const accountWords = (a: PhonakAccount | undefined) => {
  if (!a) return 'not set';
  const place = [a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [a.account && `Account ${a.account}`, a.address, place].filter(Boolean).join(' · ') || 'not set';
};
const earWord = (e: PhonakEar) => (e === 'RIGHT' ? 'Right' : 'Left');

type Fields = {
  model: string; serial_right: string; serial_left: string; receiver_size_right: string; receiver_size_left: string;
  cshell_serial_right: string; cshell_serial_left: string; original_invoice_number: string; warranty_expiration: string;
  purchase_order_number: string; contact_name: string; contact_phone: string; circumstances: string; statement_name: string;
};

function PrepareStep({ requestId, status, onDone }: { requestId: string; status: PhonakStatus; onDone: (msg: string) => void }) {
  const sides = status.request.sides || [];
  const pre = status.prefill;
  const [f, setF] = useState<Fields>(() => ({
    model: '', serial_right: '', serial_left: '', receiver_size_right: '', receiver_size_left: '', cshell_serial_right: '', cshell_serial_left: '',
    original_invoice_number: '', warranty_expiration: pre.warranty_expiration || '', purchase_order_number: '',
    contact_name: pre.contact_name || '', contact_phone: pre.contact_phone || '', circumstances: '', statement_name: pre.statement_name || '',
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(newKey());
  const set = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF((cur) => ({ ...cur, [k]: e.target.value }));

  const missing: string[] = [];
  if (!f.model.trim()) missing.push('the model');
  for (const ear of sides) if (!f[`serial_${ear.toLowerCase()}` as keyof Fields].trim()) missing.push(`the ${earWord(ear).toLowerCase()} serial number`);
  if (status.settings.purchase_order_required && !f.purchase_order_number.trim()) missing.push('the purchase order number');
  if (!f.contact_name.trim()) missing.push('the contact name');
  if (!f.contact_phone.trim()) missing.push('the contact phone');
  if (!f.circumstances.trim()) missing.push('the circumstances');
  const blocked = !status.settings.configured || !status.available;
  const ready = missing.length === 0 && !blocked;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true); setErr(null);
    const body: any = {
      model: f.model.trim(),
      original_invoice_number: f.original_invoice_number.trim(),
      warranty_expiration: f.warranty_expiration,
      purchase_order_number: f.purchase_order_number.trim(),
      contact_name: f.contact_name.trim(),
      contact_phone: f.contact_phone.trim(),
      circumstances: f.circumstances.trim(),
      statement_name: f.statement_name.trim(),
      idempotency_key: key.current,
    };
    // Only the ears the request names: the server refuses fields for an ear it does not.
    for (const ear of sides) {
      const e2 = ear.toLowerCase();
      body[`serial_${e2}`] = f[`serial_${e2}` as keyof Fields].trim();
      body[`receiver_size_${e2}`] = f[`receiver_size_${e2}` as keyof Fields];
      body[`cshell_serial_${e2}`] = f[`cshell_serial_${e2}` as keyof Fields].trim();
    }
    try {
      const r = await phonakFormApi.prepare(requestId, body);
      if (r.success && r.data) { key.current = newKey(); onDone('The Phonak form was prepared.'); } else setErr(refusalWords(r, 'Could not prepare the form.'));
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };

  const src = pre.warranty_source === 'WARRANTY_CHECK' ? 'from the warranty check' : pre.warranty_source === 'DISPENSE_HINT' ? 'estimated from the dispense date' : null;
  return (
    <form onSubmit={submit} aria-label="Prepare the Phonak form" style={stepStyle}>
      <h4>Step 1. Prepare the form</h4>
      <dl className="mf-facts">
        <dt>Patient</dt><dd>{pre.patient_name || <span className="board-na">name not on file</span>}</dd>
        <dt>Date</dt><dd>{day(`${pre.date}T12:00:00Z`)}</dd>
        <dt>Third Party Patient Number</dt><dd>{pre.third_party_patient_number || <span className="board-na">not on file</span>}</dd>
        <dt>Ship to</dt><dd>{accountWords(status.settings.ship_to)}</dd>
        <dt>Bill to</dt><dd>{accountWords(status.settings.bill_to)}</dd>
        <dt>Ears on this form</dt><dd>{sides.map(earWord).join(' and ') || '—'}</dd>
      </dl>
      <div style={gridStyle}>
        <label style={labelStyle}><span>Model</span>
          <input value={f.model} onChange={set('model')} disabled={busy} maxLength={60} required />
        </label>
        {sides.map((ear) => {
          const e2 = ear.toLowerCase();
          return (
            <React.Fragment key={ear}>
              <label style={labelStyle}><span>{earWord(ear)} serial number</span>
                <input value={f[`serial_${e2}` as keyof Fields]} onChange={set(`serial_${e2}` as keyof Fields)} disabled={busy} maxLength={40} required />
              </label>
              <label style={labelStyle}><span>{earWord(ear)} receiver/SlimTube size</span>
                <select value={f[`receiver_size_${e2}` as keyof Fields]} onChange={set(`receiver_size_${e2}` as keyof Fields)} disabled={busy}>
                  <option value="">Not given</option>
                  {['0', '1', '2', '3'].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label style={labelStyle}><span>{earWord(ear)} cShell/SlimTip serial (optional)</span>
                <input value={f[`cshell_serial_${e2}` as keyof Fields]} onChange={set(`cshell_serial_${e2}` as keyof Fields)} disabled={busy} maxLength={40} />
              </label>
            </React.Fragment>
          );
        })}
        <label style={labelStyle}><span>Original invoice number (optional)</span>
          <input value={f.original_invoice_number} onChange={set('original_invoice_number')} disabled={busy} maxLength={40} />
        </label>
        <label style={labelStyle}><span>Warranty expiration</span>
          <input type="date" value={f.warranty_expiration} onChange={set('warranty_expiration')} disabled={busy} />
          {src && <span className="mf-sub">{src[0].toUpperCase() + src.slice(1)}. Correct it if the warranty check says otherwise.</span>}
        </label>
        <label style={labelStyle}><span>{status.settings.purchase_order_required ? 'Purchase order number' : 'Purchase order number (optional)'}</span>
          <input value={f.purchase_order_number} onChange={set('purchase_order_number')} disabled={busy} maxLength={40} required={status.settings.purchase_order_required} />
        </label>
        <label style={labelStyle}><span>Contact name</span>
          <input value={f.contact_name} onChange={set('contact_name')} disabled={busy} maxLength={80} required />
        </label>
        <label style={labelStyle}><span>Contact phone</span>
          <input value={f.contact_phone} onChange={set('contact_phone')} disabled={busy} maxLength={30} inputMode="tel" required />
        </label>
      </div>
      <label style={labelStyle}>
        <span>Describe the circumstances under which the instrument was lost or damaged and the attempts made to recover it</span>
        <textarea rows={4} style={{ resize: 'vertical' }} maxLength={CIRC_MAX} value={f.circumstances} onChange={set('circumstances')} disabled={busy} required aria-describedby="phonak-circ-count" />
      </label>
      <span id="phonak-circ-count" className="mf-sub num" style={{ alignSelf: 'flex-end', marginTop: -6 }}>{f.circumstances.length} / {CIRC_MAX}</span>
      <label style={labelStyle}>
        <span>Name in the statement &quot;I, ___, hereby state ...&quot; (the person who signs; leave blank to have them write it)</span>
        <input value={f.statement_name} onChange={set('statement_name')} disabled={busy} maxLength={80} />
      </label>
      <p className="mf-sub">Do not type Social Security, Medicaid, card or bank account numbers anywhere on this form. eMerge refuses them.</p>
      {!ready && !blocked && <span className="mf-sub">Still needed: {missing.join('; ')}.</span>}
      {err && <span className="mf-note warn" role="alert">{err}</span>}
      <span className="mf-acts">
        <button type="submit" className="board-btn board-btn--primary" disabled={!ready || busy}>{busy ? 'Preparing…' : 'Prepare Phonak form'}</button>
      </span>
    </form>
  );
}

function SendStep({ requestId, status, onDone, reload }: { requestId: string; status: PhonakStatus; onDone: (msg: string) => void; reload: () => void }) {
  const send = status.send;
  const newest = status.signed[0] || null;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [already, setAlready] = useState<{ at?: string; to?: string[]; mode?: string; outcome?: string } | null>(null);
  const key = useRef(newKey());
  // Keyboard focus follows the button that replaces the one pressed: Send -> Send again, and back.
  const sendRef = useRef<HTMLButtonElement | null>(null);
  const againRef = useRef<HTMLButtonElement | null>(null);
  const wasAsking = useRef(false);
  useEffect(() => {
    if (already) {
      wasAsking.current = true;
      if (againRef.current) againRef.current.focus();
    } else if (wasAsking.current) {
      wasAsking.current = false;
      if (sendRef.current) sendRef.current.focus();
    }
  }, [already]);
  const reviewTo = send.review_to || REVIEW_ADDRESS;
  const cc = send.cc.join(', ');

  const go = async (confirm: boolean) => {
    if (!newest || busy) return;
    if (confirm) key.current = newKey();
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await phonakFormApi.send(requestId, { document_id: newest.id, idempotency_key: key.current, ...(confirm ? { confirm_resend: true } : {}) });
      if (r.success && r.data) {
        setAlready(null);
        const d = r.data;
        let words: string;
        if (d.outcome === 'SENT') {
          words = d.mode === 'live'
            ? `Sent to Phonak (${d.to.join(', ')})${d.cc && d.cc.length ? `, copy to ${d.cc.join(', ')}` : ''}.`
            : `Review copy sent to ${d.to.join(', ')} only. It did not go to Phonak.`;
        } else words = d.message;
        setResult(words);
        key.current = newKey();
        onDone(words);
      } else if (r.code === 'ALREADY_SENT') {
        const dt = r.detail || {};
        setAlready({ at: dt.at, to: Array.isArray(dt.to) ? dt.to : [], mode: dt.mode, outcome: dt.outcome });
      } else {
        setErr(refusalWords(r, 'Could not send the form.'));
        if (r.code === 'STALE_DOCUMENT' || r.code === 'SEND_IN_FLIGHT') reload();
      }
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };

  let body: React.ReactNode;
  if (send.mode === 'off') {
    body = <p className="mf-sub">Sending the Phonak form from eMerge is not switched on yet.</p>;
  } else {
    const modeLine = send.mode === 'review'
      ? `Review mode: the email goes only to ${reviewTo}. It would go to ${send.live_to || 'the Phonak contact'}${cc ? ` with a copy to ${cc}` : ''}.`
      : `Goes to ${send.live_to || 'the Phonak contact'}${cc ? `, copy to ${cc}` : ''}; replies come to you.`;
    const label = send.mode === 'review' ? `Send review copy to ${reviewTo}` : 'Send to Phonak';
    let why: string | null = null;
    if (status.in_flight) why = 'Being sent now; reload in a moment.';
    else if (!newest) why = 'Upload the signed form first. Only a signed form is sent.';
    else if (!send.enabled) why = send.reason || 'Sending is not available right now.';
    body = (
      <>
        <p className="mf-sub">{modeLine}</p>
        {why && <p className="mf-note warn">{why}</p>}
        {!already && (
          <span className="mf-acts">
            <button ref={sendRef} type="button" className="board-btn board-btn--primary" disabled={Boolean(why) || busy} onClick={() => go(false)}>{busy ? 'Sending…' : label}</button>
          </span>
        )}
        {already && (
          <div className="mf-panel" role="group" aria-label="Already sent">
            <p className="mf-note warn">
              Already sent ({already.mode || 'unknown mode'}) on {day(already.at)}{already.to && already.to.length ? ` to ${already.to.join(', ')}` : ''}. Send again?
            </p>
            <span className="mf-acts">
              <button ref={againRef} type="button" className="board-btn board-btn--primary" disabled={busy} onClick={() => go(true)}>{busy ? 'Sending…' : 'Send again'}</button>
              <button type="button" className="board-btn" disabled={busy} onClick={() => setAlready(null)}>Do not send again</button>
            </span>
          </div>
        )}
      </>
    );
  }
  return (
    <section style={stepStyle} aria-label="Send the signed form">
      <h4>Step 3. Send</h4>
      {body}
      {result && <p className="mf-notice" role="status">{result}</p>}
      {err && <span className="mf-note warn" role="alert">{err}</span>}
    </section>
  );
}

export default function PhonakFormPanel({ requestId, onChanged }: { requestId: string; onChanged?: (msg?: string) => void }) {
  const [status, setStatus] = useState<PhonakStatus | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [openErr, setOpenErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await phonakFormApi.status(requestId);
      if (r.success && r.data && r.data.request) { setStatus(r.data); setProblem(null); } else setProblem(refusalWords(r, 'Could not load the Phonak form.'));
    } catch { setProblem('Could not reach eMerge.'); }
  }, [requestId]);
  useEffect(() => { setStatus(null); setProblem(null); load(); }, [load]);

  // The send step shows its own result line, so the panel's note is for the other two steps only.
  const changed = (msg: string, showNote = true) => { if (showNote) setNote(msg); load(); if (onChanged) onChanged(); };

  const open = async (docId: string) => {
    setOpenErr(null);
    const e = await phonakFormApi.openDocument(requestId, docId);
    if (e) setOpenErr(e);
  };

  if (problem && !status) {
    return <div className="mf-panel"><p className="mf-note warn" role="alert">{problem}</p><span className="mf-acts"><button type="button" className="board-btn" onClick={load}>Try again</button></span></div>;
  }
  if (!status) return <div className="mf-panel"><p className="mf-sub" role="status">Loading the Phonak form…</p></div>;

  const fact = phonakFactWords({ last_live: status.last_sent.live, last_review: status.last_sent.review });
  const newestPrepared = status.prepared[0] || null;
  const blockers: string[] = [];
  if (status.refusal) blockers.push(status.refusal.message);
  if (!status.settings.configured) blockers.push(`The Phonak account details are not configured yet (missing: ${status.settings.missing.join('; ') || 'unknown'}). Isaiah supplies them.`);
  if (status.provider.message) blockers.push(status.provider.message);

  return (
    <section className="mf-panel" aria-label="Phonak courtesy replacement form" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {fact && <p className="mf-notice" role="status">{fact}</p>}
      {blockers.map((b) => <p key={b} className="mf-note warn">{b}</p>)}
      <p className="mf-sub">Reference {status.request.reference}. {status.request.reason_words}</p>
      {status.provider.email && (
        <p className="mf-sub">
          Provider copied: {status.provider.name || status.provider.email} ({status.provider.email}) - {status.provider.why || PROVIDER_SOURCE[status.provider.source || ''] || 'on record'}.
        </p>
      )}
      {note && <p className="mf-notice" role="status">{note}</p>}

      <PrepareStep requestId={requestId} status={status} onDone={(m) => changed(m)} />

      <section style={stepStyle} aria-label="The prepared form">
        <h4>The prepared form</h4>
        {status.prepared.length === 0 && <p className="mf-sub">Nothing is prepared yet.</p>}
        {newestPrepared && (
          <>
            <p className="mf-sub">
              Prepared {day(newestPrepared.at)}{newestPrepared.by_name ? ` by ${newestPrepared.by_name}` : ''}{newestPrepared.pages ? ` (${newestPrepared.pages} ${newestPrepared.pages === 1 ? 'page' : 'pages'})` : ''}.
            </p>
            <span className="mf-acts">
              <button type="button" className="board-btn" onClick={() => open(newestPrepared.id)}>Open the prepared PDF</button>
            </span>
            <p className="mf-sub">Print it, have the patient or responsible party and the hearing care professional sign and date it, scan it to PDF, and upload the signed scan below.</p>
          </>
        )}
        {status.prepared.slice(1).map((d: PhonakDoc) => (
          <span key={d.id} className="mf-sub">
            Earlier form, {day(d.at)}{d.by_name ? ` by ${d.by_name}` : ''}{' '}
            <button type="button" className="board-btn" aria-label={`Open the earlier prepared form from ${day(d.at)}`} onClick={() => open(d.id)}>Open</button>
          </span>
        ))}
        {openErr && <span className="mf-note warn" role="alert">{openErr}</span>}
      </section>

      <UploadStep requestId={requestId} status={status} onDone={(m) => changed(m)} open={open} />
      <SendStep requestId={requestId} status={status} onDone={(m) => changed(m, false)} reload={load} />

      {status.sends.length > 0 && (
        <section style={stepStyle} aria-label="Sends so far">
          <h4>Sends so far</h4>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {status.sends.map((s, i) => (
              <li key={`${s.at}-${i}`} className="mf-sub">
                {s.mode === 'live' ? 'Live' : 'Review'} · {s.outcome === 'SENT' ? 'sent' : s.outcome === 'FAILED' ? 'did not go' : 'may have been sent'} · {day(s.at)} · to {s.to.join(', ') || '—'}{s.by_name ? ` · by ${s.by_name}` : ''}
                {s.mode !== 'live' && ' (review copy: not sent to Phonak)'}
              </li>
            ))}
          </ul>
        </section>
      )}
      <p className="mf-sub">{status.umed_help}</p>
    </section>
  );
}

function UploadStep({ requestId, status, onDone, open }: { requestId: string; status: PhonakStatus; onDone: (msg: string) => void; open: (id: string) => void }) {
  const newestPrepared = status.prepared[0] || null;
  const [file, setFile] = useState<File | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const key = useRef(newKey());
  const input = useRef<HTMLInputElement | null>(null);

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setErr(null);
    if (f && f.size > SIGNED_MAX_BYTES) { setFile(null); setErr(REFUSAL_FALLBACK.FILE_TOO_LARGE); return; }
    setFile(f);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !confirmed || !newestPrepared || busy) return;
    setBusy(true); setErr(null);
    const form = new FormData();
    form.append('file', file);
    form.append('prepared_id', newestPrepared.id);
    form.append('confirm_signed', 'true');
    form.append('idempotency_key', key.current);
    try {
      const r = await phonakFormApi.uploadSigned(requestId, form);
      if (r.success && r.data) {
        key.current = newKey(); setFile(null); setConfirmed(false);
        if (input.current) input.current.value = '';
        onDone('The signed form was uploaded.');
      } else setErr(refusalWords(r, 'Could not upload the signed form.'));
    } catch { setErr('Could not reach eMerge. Try again.'); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} aria-label="Upload the signed form" style={stepStyle}>
      <h4>Step 2. Upload the signed form</h4>
      {!newestPrepared && <p className="mf-sub">Prepare the form first; then upload the signed scan here.</p>}
      <label style={labelStyle}><span>Signed form (PDF, 2.5 MB at most)</span>
        <input ref={input} type="file" accept=".pdf,application/pdf" onChange={pick} disabled={busy || !newestPrepared} />
      </label>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={busy || !newestPrepared} />
        <span>This is the prepared form, signed by the patient or responsible party and by the hearing care professional</span>
      </label>
      {err && <span className="mf-note warn" role="alert">{err}</span>}
      <span className="mf-acts">
        <button type="submit" className="board-btn board-btn--primary" disabled={!file || !confirmed || !newestPrepared || busy}>{busy ? 'Uploading…' : 'Upload signed form'}</button>
      </span>
      {status.signed.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {status.signed.map((d) => (
            <li key={d.id} className="mf-sub">
              Signed form uploaded {day(d.at)}{d.by_name ? ` by ${d.by_name}` : ''}{' '}
              <button type="button" className="board-btn" aria-label={`Open the signed form from ${day(d.at)}`} onClick={() => open(d.id)}>Open</button>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}
